import { Avatar, Group, Text } from '@mantine/core'
import React, { forwardRef, memo, useRef, useState } from 'react'

import setaDropdownVerde from '../../../icones/icone-seta-dropdown-verde.png'
import limparVerde from '../../../icones/iconesVersaoVermelha/icone-fechar-vermelho.svg'
import Loader from '../Loader'
import Tooltip from '../Tooltip'

import * as S from './styles'
import { GlobalStyles } from './styles'

/**
 * @typedef {Object} Option
 * @property {string} label
 * @property {string} value
 * @property {string} [image]
 * @property {string} [description]
 */

/**
 * @typedef {Object} SelectProps
 * @property {string | null | undefined} [value]
 * @property {(value: string | null) => void} [onChange]
 * @property {string} [placeholder]
 * @property {React.ReactNode} [label]
 * @property {string | number} [width]
 * @property {Option[]} [options]
 * @property {boolean} [disabled]
 * @property {boolean} [withAsterisk]
 * @property {number} [fontSize]
 * @property {boolean} [searchable]
 * @property {boolean} [clearable]
 * @property {string} [error]
 * @property {boolean} [isLoading]
 */

/**
 * @param {SelectProps} props
 */
export default function Select({
  value,
  onChange,
  placeholder,
  label,
  width,
  options,
  disabled = false,
  withAsterisk = false,
  fontSize = 20,
  searchable = false,
  clearable = false,
  error,
  isLoading = false
}) {
  const [searchValue, setSearchChange] = useState('')
  const [isOpen, setIsOpen] = useState(false)

  const refSelect = useRef()
  const justClosedRef = useRef(false)

  const widthText = refSelect?.current?.getBoundingClientRect()?.width

  const CustomSelectItem = forwardRef((props, ref) => (
    <SelectItem ref={ref} width={widthText} {...props} />
  ))

  CustomSelectItem.displayName = 'CustomSelectItem'

  const handleClear = (e) => {
    e.stopPropagation()
    if (onChange) {
      onChange(null)
    }
  }

  const handleOpenDropdown = (e) => {
    e.stopPropagation()

    // Se acabou de fechar, ignora esse clique para não reabrir
    if (justClosedRef.current) {
      justClosedRef.current = false
      return
    }

    const wrapper = refSelect.current?.closest('.mantine-Select-wrapper')
    const input = wrapper?.querySelector('input')

    if (input) {
      input.focus()
      input.dispatchEvent(
        new MouseEvent('mousedown', { bubbles: true, cancelable: true })
      )
    }
  }

  const showClearButton = clearable && value != null && !disabled

  return (
    <>
      <GlobalStyles width={width} />
      <S.Select
        ref={refSelect}
        label={label}
        fontSize={`${fontSize}px`}
        width={width && width}
        error={error}
        placeholder={placeholder}
        itemComponent={CustomSelectItem}
        withAsterisk={withAsterisk}
        searchable={searchable}
        clearable={false}
        withinPortal
        zIndex={9999}
        radius="xl"
        value={value}
        disabled={disabled}
        onSearchChange={setSearchChange}
        searchValue={searchValue}
        nothingFound={
          isLoading && (!options || options.length === 0) ? (
            <S.LoadingWrapper>
              <Loader />
              <span>Carregando...</span>
            </S.LoadingWrapper>
          ) : (
            'Nenhuma opção encontrada'
          )
        }
        data={options || []}
        allowDeselect
        onChange={onChange}
        onDropdownOpen={() => {
          setIsOpen(true)
          justClosedRef.current = false
        }}
        onDropdownClose={() => {
          setIsOpen(false)
          justClosedRef.current = true
        }}
        rightSection={
          <S.RightSection onClick={handleOpenDropdown}>
            {showClearButton ? (
              <S.Img
                isOpen={isOpen}
                isClear={showClearButton}
                alt="limpar"
                src={limparVerde}
                onClick={(e) => {
                  e.stopPropagation()
                  handleClear(e)
                }}
              />
            ) : (
              <S.Img
                isOpen={isOpen}
                isClear={showClearButton}
                alt="seta-dropdown"
                src={setaDropdownVerde}
              />
            )}
          </S.RightSection>
        }
      />
    </>
  )
}

/**
 * @param {Object} props
 * @param {string} [props.image]
 * @param {string} props.label
 * @param {string} [props.description]
 * @param {number} [props.width]
 * @param {React.Ref<HTMLDivElement>} ref
 */
const SelectItem = memo(
  forwardRef(({ image, label, description, width, ...others }, ref) => {
    const textRef = useRef(null)

    const showTooltip = textRef?.current?.scrollWidth > width

    return (
      <Tooltip
        disabled={!showTooltip}
        width={width}
        withArrow
        label={label}
        position="bottom"
      >
        <div ref={ref} {...others}>
          <Group noWrap>
            {image && <Avatar src={image} />}
            <div style={{ width }}>
              <S.TextItem addOverflow={showTooltip} ref={textRef}>
                {label}
              </S.TextItem>
              {description && (
                <Text size="xs" opacity={0.65}>
                  {description}
                </Text>
              )}
            </div>
          </Group>
        </div>
      </Tooltip>
    )
  })
)
SelectItem.displayName = 'SelectItem'
